// vueui/src/stores/adminSettings.ts

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getToken, handleGlobalAuthError } from '@/assets/common'
import { API_ENDPOINTS, getApiUrl, apiRequest } from '@/assets/api'

export interface AdminSettings {
  notificationPollingInterval: number
  pdfGenerationTimeout: number
  pdfMemoryAllocation: number
  autoPdfGeneration: boolean
  imageQualityThreshold: number
  cloudFrontCacheDuration: number
  sessionTimeout: number
  emailDeliveryRetries: number
  analyticsCollection: boolean
  metricsRetentionDays: number
  dashboardRefreshInterval: number
  logLevel: string
}

export const useAdminSettingsStore = defineStore('adminSettings', () => {
  const settings = ref<AdminSettings | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const updating = ref(false)

  // Computed properties
  const isLoaded = computed(() => settings.value !== null)
  const hasError = computed(() => error.value !== null)

  // Actions
  const fetchSettings = async () => {
    loading.value = true
    error.value = null

    try {
      const url = getApiUrl(API_ENDPOINTS.ADMIN_SETTINGS.GET_ADMIN_SETTINGS)
      
      const response = await apiRequest(url)

      if (!response.ok) {
        if (response.status === 401) {
          handleGlobalAuthError()
          return
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      settings.value = data.settings
      console.log('✅ Admin settings loaded:', settings.value)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      console.error('❌ Error fetching admin settings:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateSettings = async (newSettings: Partial<AdminSettings>) => {
    if (!settings.value) {
      throw new Error('Settings not loaded')
    }

    updating.value = true
    error.value = null

    try {
      const url = getApiUrl(API_ENDPOINTS.ADMIN_SETTINGS.UPDATE_ADMIN_SETTINGS)
      
      const response = await apiRequest(url, {
        method: 'PUT',
        body: { settings: newSettings }
      })

      if (!response.ok) {
        if (response.status === 401) {
          handleGlobalAuthError()
          return
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      
      // Update local settings with the new values
      if (settings.value) {
        Object.assign(settings.value, newSettings)
      }
      
      console.log('✅ Admin settings updated:', data.updatedSettings)
      return data

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      error.value = errorMessage
      console.error('❌ Error updating admin settings:', err)
      throw err
    } finally {
      updating.value = false
    }
  }

  const resetSettings = () => {
    settings.value = null
    error.value = null
    loading.value = false
    updating.value = false
  }

  // Helper function to get a specific setting value
  const getSetting = (key: keyof AdminSettings): any => {
    return settings.value?.[key] ?? null
  }

  // Helper function to format setting values for display
  const formatSetting = (key: keyof AdminSettings): string => {
    const value = getSetting(key)
    if (value === null) return 'Not set'
    
    switch (key) {
      case 'notificationPollingInterval':
        return `${value / 1000}s`
      case 'pdfGenerationTimeout':
        return `${value / 1000}s`
      case 'pdfMemoryAllocation':
        return `${value}MB`
      case 'cloudFrontCacheDuration':
        return `${value / 3600}h`
      case 'sessionTimeout':
        return `${value / 3600000}h`
      case 'dashboardRefreshInterval':
        return `${value / 1000}s`
      case 'metricsRetentionDays':
        return `${value} days`
      case 'emailDeliveryRetries':
        return `${value} retries`
      case 'imageQualityThreshold':
        return `${value} PPI`
      default:
        return String(value)
    }
  }

  return {
    // State
    settings,
    loading,
    error,
    updating,
    
    // Computed
    isLoaded,
    hasError,
    
    // Actions
    fetchSettings,
    updateSettings,
    resetSettings,
    getSetting,
    formatSetting
  }
})
